import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType, CreateEffectMetadata} from '@ngrx/effects';
import {AuthService} from '~core/security/auth.service';
import {authSet, FeatureAuthActionsTypes} from './feature-auth.actions';
import {catchError, mergeMap, observeOn, tap} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {AuthNgrx, getRoles} from '~model/ngrx/auth-ngrx';
import {MatSnackBar} from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthVo } from '~model/vo/auth-vo';
import {asyncScheduler, BehaviorSubject, Observable} from 'rxjs';

@Injectable()
export class FeatureAuthEffects {

  readonly login$: Observable<void | AuthNgrx> & CreateEffectMetadata = createEffect(() => this.actions$.pipe(
    ofType(FeatureAuthActionsTypes.Login),
    mergeMap((action: AuthVo) =>
      this.loginProcess(this.authService.conectar(action), false).pipe(observeOn(asyncScheduler, 2500))
    )
  ), {dispatch: false});

  readonly loginToken$: Observable<void | AuthNgrx> & CreateEffectMetadata = createEffect(() => this.actions$.pipe(
    ofType(FeatureAuthActionsTypes.LoginToken),
    mergeMap(({token}: { token: string, tokenAuthObservable: BehaviorSubject<boolean> }) =>
      this.loginProcess(
        this.authService.conectarToken({ token }).pipe(observeOn(asyncScheduler, 1000))
        , true
      )
    )
  ), {dispatch: false});

  constructor(
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly store: Store<AuthNgrx>,
    private readonly snackbar: MatSnackBar
  ) {
  }

  loginProcess(loginObservable: Observable<AuthNgrx>, tokenAuth: boolean): Observable<void | AuthNgrx> {
    return loginObservable.pipe(
      tap(res => {
        if (res?.logout) {
          this.authService.nextTokenAuth('LOGOUT');
        } else if (res?.token && tokenAuth) {
          this.authService.nextTokenAuth('SUCCESS');
        }
      }),
      observeOn(asyncScheduler, 1500),
      tap(res => {
        if (res?.token) {
          this.authService.nextTokenAuth('SUCCESS');
        } else if (!res?.logout) {
          this.authService.nextTokenAuth('INVALID');
        }
      }),
      tap(res => this.authService.ativar(res)),
      tap(({ email, token, tokenRules, nomeEmpresa, linkLogo }) => {
        const tokenDeconded = this.authService.jwt.decodeToken(token ?? '');
        const [nickname, empresa] = tokenDeconded.sub.split('@');
        const roles = getRoles(token ?? '');
        this.store.dispatch(authSet({ email, nickname, token, tokenRules, empresa, nomeEmpresa, linkLogo, roles }) as any);
        this.authService.nextTokenAuth('NONE');
      }),
      catchError((err: HttpErrorResponse) => {
        // VERIFICAÇÃO ESPECÍFICA PARA ERRO 401 - SEM SNACKBAR
        const is401Error = err.status === 401 ||
                          err.statusText === 'Unauthorized' ||
                          err.message?.includes('401') ||
                          err.error?.status === 401 ||
                          // Se o status é undefined mas sabemos que é um erro de auth, tratamos como 401
                          (err.status === undefined && err.message?.includes('not iterable'));

        if (is401Error) {
          this.authService.nextTokenAuth('INVALID');
          // Retorna observable que completa imediatamente sem emitir valores
          return new Observable<void>(subscriber => {
            subscriber.complete();
          });
        }

        // Para outros erros (não 401), mostramos o snackbar
        let message = 'Ops! ocorreu um erro inesperado';
        if (err.status === 0) {
          message = navigator.onLine ? 'Ops! Serviço indisponível. Tente novamente em instantes.'
            : 'Ops! Confira sua conexão com internet';
        } else if (err.status >= 500) {
          message = 'Erro interno do servidor. Tente novamente em instantes.';
        }

        this.snackbar.open(message, 'OK', { duration: 3000 });

        return new Observable<void>(subscriber => {
          subscriber.complete();
        });
      })
    );
  }

}
